import { Component, inject, Input, OnInit } from '@angular/core';
import _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { ContactCardViewComponent } from '.';
import { ContactCardCreateInput, ContactCardUpdateInput, Uuid } from '../../../../../graphql';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { DialogParams, DialogService } from '../../../../core/services/dialog.service';
import { PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import {
    ContactCardLegacy,
    ContactLegacy,
    ContactsLegacyConvertor,
} from '../../../../data/repositories/contacts/contacts-legacy-convertor.service';
import { ContactApiService } from '../../../../data/repositories/contacts/contacts.api.service';
import { RepoContactsService } from '../../../../data/repositories/repo-contacts.service';
import { RepoRemindersService } from '../../../../data/repositories/repo-reminders.service';
import { newUuid } from '../../../../utils/helper-functions/new-uuid';

@Component({
    selector: 'app-contact-cards',
    templateUrl: './contact-cards.component.html',
    standalone: false,
})
export class ContactCardsComponent implements OnInit {
    #api = inject(ContactApiService);
    #convertor = inject(ContactsLegacyConvertor);
    #logger = inject(BxLogger);

    @Input() contact: ContactLegacy = null!;

    // @ts-ignore TS2564
    bsModalRef: BsModalRef;
    title = 'Cards';
    createNewLabel = 'Create New';
    showCreateNew = true;
    createNewEnabled = true;

    contactCardsForPresets!: string;

    constructor(
        private contacts: RepoContactsService,
        private reminders: RepoRemindersService,
        private storesService: StoresService,
        public permissionService: PermissionService,
        private modalService: BsModalService,
        private dialogService: DialogService
    ) {}

    ngOnInit() {
        this.updateContactCardsForPresets();
    }

    updateContactCardsForPresets() {
        const data = this.contact.ContactCards.map((c) => {
            return {
                type: c.CardType,
                name: c.CardName,
                details: c.CardDetails,
            };
        });

        this.contactCardsForPresets = JSON.stringify(data);
    }

    addCard() {
        let newCard = { ID_Contact: this.contact.ID_Contact!, ID_ContactCard: newUuid(), isNew: true };
        this.viewCard(newCard);
    }

    // @ts-ignore TS7006
    deleteCard(evt, card) {
        evt.stopPropagation();
        const params = {
            title: 'Delete this card?',
            text: `Confirm you want to delete ${card.CardName}?`,
        };
        this.dialogService.show(params).then(
            () => {
                this._deleteCard(card);
            },
            () => ({})
        );
    }

    _deleteCard(card: ContactCardLegacy) {
        card.IsDeleted = true;
        this.#api.deleteContactCards({ input: { contactPersonIds: [card.ID_ContactCard] } }).subscribe(() => {
            this.contact.ContactCards = this.contact.ContactCards.filter((x) => x.ID_ContactCard !== card.ID_ContactCard);
            this.#saveCardGql(card);
        });
    }

    viewCard(orgCard: Partial<ContactCardLegacy> & { isNew?: boolean }) {
        const card = { ...orgCard };
        this.bsModalRef = this.modalService.show(ContactCardViewComponent, { initialState: card });
        this.bsModalRef.content.positiveAction.pipe(first()).subscribe(() => {
            if (!card.ID_Contact || !card.ID_ContactCard) return this.bsModalRef.hide();
            const cardModel = {
                iD_ContactCard: card.ID_ContactCard,
                cardType: card.CardType,
                cardName: card.CardName,
                cardDetails: card.CardDetails,
                expiry: card.Expiry,
            };
            if (card.isNew) this.#createCard({ ...cardModel, iD_Contact: card.ID_Contact });
            else this.#updateCard(cardModel);
            return this.bsModalRef.hide();
        });
        this.bsModalRef.content.negativeAction.pipe(first()).subscribe(() => {
            this.syncCardReminder(orgCard);
            this.bsModalRef.hide();
            return this.bsModalRef.hide();
        });
    }
    #updateCard(card: ContactCardUpdateInput) {
        return this.#api.updateContactCards({ input: { models: [card] } }).subscribe((x) => {
            const contactCard = x.data?.updateContactCards.contactCard?.at(0);
            if (!contactCard) return;
            const legacyCard = this.#convertor.mapToLegacyContactCard(contactCard);
            const index = this.contact.ContactCards.findIndex((x) => x.ID_ContactCard === contactCard.ID_ContactCard);
            this.contact.ContactCards[index] = legacyCard;
            this.#saveCardGql(legacyCard);
        });
    }
    #createCard(card: ContactCardCreateInput) {
        return this.#api.createContactCards({ input: { models: [card] } }).subscribe((x) => {
            const contactCard = x.data?.createContactCards.contactCard?.at(0);
            if (!contactCard) return;
            const legacyCard = this.#convertor.mapToLegacyContactCard(contactCard);
            this.contact.ContactCards.unshift(legacyCard);
            this.#saveCardGql(legacyCard);
        });
    }

    #saveCardGql(card: ContactCardLegacy) {
        this.syncCardReminder(card);
        this.updateContactCardsForPresets();
        this.storesService.refreshContacts();
        this.#convertor.setCardExpiryToClosest(this.contact);
        this.#logger.success('Card Saved');
    }

    syncCardReminder(card: Readonly<Partial<ContactCardLegacy>>) {
        if ((card as any)?.entityAspect?.entityState?.isDetached()) return;

        this.reminders
            .GetByReference(this.contact.ID_Contact!, {
                childReferenceId: card.ID_ContactCard,
            })
            .then((data) => {
                let reminder;

                if (data.results && data.results.length) reminder = data.results[0];

                if (card.IsDeleted || !card.Expiry) {
                    if (reminder) {
                        reminder.IsDeleted = true;
                        this.reminders.Save(reminder);
                    }
                } else {
                    if (!reminder) {
                        reminder = this.reminders.CreateReminder({
                            ID_Reference: this.contact.ID_Contact,
                            ID_ChildReference: card.ID_ContactCard,
                        });
                    }
                    reminder.DueDate = card.Expiry;
                    reminder.ModuleReference = 'contacts.view.cards';
                    reminder.OpenData = JSON.stringify({
                        id: card.ID_Contact,
                    });
                    reminder.Description = this.contact.Description + ': ' + card.CardName + ' due to expire';
                    reminder.IsCompleted = false;
                    reminder.SendSMS = true;
                    reminder.SendEmail = true;
                    reminder.IsDeleted = false;
                    reminder.SendSMSTime = new Date(
                        reminder.DueDate.getFullYear(),
                        reminder.DueDate.getMonth(),
                        reminder.DueDate.getDate(),
                        9,
                        0,
                        0,
                        0
                    ); // default send time to 9am

                    this.reminders.Save(reminder);
                }
            });
    }
    applyPreset(preset: any) {
        const params: DialogParams = {
            title: "Overwrite this contact's existing cards?",
            text: `${preset.Description} will be applied to this contact`,
            type: 'warning',
        };
        this.dialogService.show(params).then(
            () =>
                this._deleteAllCards().then(() => {
                    const data: { type: string; name: string; details: string }[] = JSON.parse(preset);
                    this.#api
                        .createContactCards({
                            input: {
                                models: data.map((preset) => ({
                                    iD_Contact: this.contact.ID_Contact!,
                                    iD_ContactCard: newUuid(),
                                    cardType: preset.type,
                                    cardName: preset.name,
                                    cardDetails: preset.details,
                                })),
                            },
                        })
                        .subscribe((x) => {
                            this.contact.ContactCards =
                                x?.data?.createContactCards.contactCard?.map((c) => this.#convertor.mapToLegacyContactCard(c)) ?? [];
                            this.#logger.success('Card Saved');
                        });
                }),
            () => ({})
        );
    }

    _deleteAllCards() {
        return this.reminders.GetByReference(this.contact.ID_Contact!).then((data) => {
            const reminders = data.results;
            const cardIds = [] as Uuid[];
            for (let i = this.contact.ContactCards.length - 1; i >= 0; i--) {
                const card = this.contact.ContactCards[i]!;
                cardIds.push(card.ID_ContactCard);
                const reminder = _.find(reminders, {
                    ID_ChildReference: card.ID_ContactCard,
                });
                if (reminder) reminder.IsDeleted = true;
            }
            return firstValueFrom(this.#api.deleteContactCards({ input: { contactPersonIds: cardIds } })).then((x) => {
                this.contact.ContactCards = [];
                this.updateContactCardsForPresets();
                this.#convertor.setCardExpiryToClosest(this.contact);
                this.reminders.Save();
                this.#logger.success('Card Saved');
            });
        });
    }
}
